/**
 * Anteprima diff per l'import listino: confronta i row del file
 * con i materiali esistenti del fornitore in DB, classifica in:
 *
 *  - newItems   : presenti nel file, NON in DB → saranno inseriti
 *  - changedListPrice : codice già in DB ma list_price diverso
 *                       (delta >= 0.01)
 *  - unchanged  : codice in DB, list_price identico
 *  - removed    : codice presente in DB e NON nel file
 *                 (saranno archiviati SOLO se archive_missing=true)
 *
 * Best-effort: l'import in produzione usa `useImportMaterials` per la
 * scrittura, qui solo report read-only.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ParsedMaterial } from '@/lib/import/saintGobainParser';

export interface ImportDiffRow {
  code: string;
  name: string;
  fileListPrice: number | null;
  dbListPrice: number | null;
  /** delta % calcolato come (file - db) / db × 100. null se uno dei due manca. */
  deltaPct: number | null;
}

export interface ImportDiffSummary {
  newItems: ImportDiffRow[];
  changedListPrice: ImportDiffRow[];
  unchanged: ImportDiffRow[];
  removed: ImportDiffRow[];
}

const EPSILON = 0.01;

function safeNum(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function useImportDiff(
  supplierId: string | null,
  rows: ParsedMaterial[] | null,
): { data: ImportDiffSummary | null; isLoading: boolean } {
  const enabled = !!supplierId && !!rows && rows.length > 0;
  const codes = (rows ?? []).map((r) => r.code);

  const query = useQuery<ImportDiffSummary | null>({
    queryKey: ['import-diff', supplierId, codes.length, codes[0], codes[codes.length - 1]],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      if (!enabled || !rows || !supplierId) return null;

      // Carica tutti i materiali attivi del fornitore per matching e per
      // calcolare i "removed". Filtriamo per codice in batch evitiamo timeout.
      const { data: dbRows, error } = await supabase
        .from('materials')
        .select('code, name, list_price')
        .eq('supplier_id', supplierId)
        .eq('is_active', true);
      if (error) throw error;

      const dbMap = new Map(
        (dbRows ?? []).map((m) => [
          m.code,
          {
            code: m.code,
            name: m.name as string,
            list_price: safeNum(m.list_price),
          },
        ]),
      );

      const fileCodeSet = new Set(rows.map((r) => r.code));
      const newItems: ImportDiffRow[] = [];
      const changedListPrice: ImportDiffRow[] = [];
      const unchanged: ImportDiffRow[] = [];

      for (const r of rows) {
        const fileListPrice = safeNum(r.list_price);
        const dbItem = dbMap.get(r.code);
        if (!dbItem) {
          newItems.push({
            code: r.code,
            name: r.name,
            fileListPrice,
            dbListPrice: null,
            deltaPct: null,
          });
          continue;
        }
        if (
          dbItem.list_price != null &&
          fileListPrice != null &&
          Math.abs(fileListPrice - dbItem.list_price) >= EPSILON
        ) {
          const deltaPct = dbItem.list_price > 0
            ? ((fileListPrice - dbItem.list_price) / dbItem.list_price) * 100
            : null;
          changedListPrice.push({
            code: r.code,
            name: r.name,
            fileListPrice,
            dbListPrice: dbItem.list_price,
            deltaPct,
          });
        } else {
          unchanged.push({
            code: r.code,
            name: r.name,
            fileListPrice,
            dbListPrice: dbItem.list_price,
            deltaPct: 0,
          });
        }
      }

      // Removed: presenti in DB ma non nel file
      const removed: ImportDiffRow[] = [];
      for (const [code, dbItem] of dbMap) {
        if (!fileCodeSet.has(code)) {
          removed.push({
            code,
            name: dbItem.name,
            fileListPrice: null,
            dbListPrice: dbItem.list_price,
            deltaPct: null,
          });
        }
      }

      return { newItems, changedListPrice, unchanged, removed };
    },
  });

  return { data: query.data ?? null, isLoading: query.isLoading };
}
